import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useRef } from "react"

import { pageHead } from "@/lib/metadata"
import { getHelp } from "@/server/web"
import Link from "@/components/link"
import { BookOpen, ChevronRight, Search } from "lucide-react"

import { EmptyState } from "@anysola/ui/empty-state"
import { UnavailableAlert } from "@/components/feedback/unavailable-alert"
import { HelpFrame, sectionIcon, sectionId } from "@/components/help/help-frame"
import { RowIcon } from "@anysola/ui/row-group"
import { SiteShell } from "@/components/layout/site-shell"
import { Button } from "@anysola/ui/button"
import type { PublicKbArticle } from "@anysola/contracts/web/kb-core"
import { ACCOUNT_URL } from "@/lib/app-links"
import { groupKbArticles } from "@anysola/contracts/web/kb-core"
import { HelpLoading } from "@/components/help/help-loading"
import { StillStuck } from "@/components/help/still-stuck"

/**
 * One section as a collection card: its icon, name and count, then each
 * article as a row inside (title and a one-line summary, the whole row a
 * link).
 */
function ArticleGroup({
  name,
  articles,
}: {
  name: string
  articles: PublicKbArticle[]
}) {
  const id = sectionId(name)
  return (
    <section
      aria-labelledby={id}
      className="min-w-0 scroll-mt-20 rounded-surface bg-card p-2"
    >
      <div className="flex items-center gap-3 px-3 pt-2 pb-3">
        <RowIcon>{sectionIcon(name)}</RowIcon>
        <h2
          id={id}
          className="flex-1 text-[0.9375rem] font-medium text-foreground"
        >
          {name}
        </h2>
        <span className="text-sm text-muted-foreground tabular-nums">
          {articles.length}
        </span>
      </div>
      <ul className="grid grid-cols-[minmax(0,1fr)] gap-0.5">
        {articles.map((article) => (
          <li
            key={article.id}
            className="group/row relative flex items-center gap-3 rounded-xl px-3 py-3 transition-colors hover:bg-surface-secondary"
          >
            <div className="min-w-0 flex-1">
              <Link
                href={`/help/${article.slug}`}
                className="block text-sm text-foreground outline-none after:absolute after:inset-0 after:rounded-[inherit] focus-visible:after:ring-2 focus-visible:after:ring-ring"
              >
                {article.title}
              </Link>
              <p className="mt-0.5 truncate text-[13px] text-muted-foreground">
                {article.summary}
              </p>
            </div>
            <ChevronRight
              aria-hidden="true"
              className="size-4 shrink-0 text-muted-foreground/60 transition-transform group-hover/row:translate-x-0.5"
            />
          </li>
        ))}
      </ul>
    </section>
  )
}

function HelpPage() {
  const { q } = Route.useSearch()
  const query = q?.trim().slice(0, 100) || undefined
  const { articles, agentOn } = Route.useLoaderData()
  // With the agent on, a new ticket gets an answer in moments rather than hours.
  const contactLabel = agentOn ? "Ask the AI assistant" : "Contact support"
  const groups = articles && !query ? groupKbArticles(articles) : []
  const navigate = Route.useNavigate()
  // The page stays mounted when only `?q` changes (Clear, Back), so the
  // field follows the URL instead of keeping what was typed last.
  const field = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (field.current) field.current.value = query ?? ""
  }, [query])

  return (
    <SiteShell>
      <h1 className="sr-only">Help center</h1>

      <HelpFrame
        counts={
          articles && !query
            ? new Map(groups.map((g) => [g.name, g.articles.length]))
            : undefined
        }
      >
        {/* Search is what most visitors came to do, so it opens the page. A
            visible submit: Enter alone was the only way to search, which
            nothing on the page said. */}
        <form
          role="search"
          className="relative"
          // Through the router: the browser's own GET submit reloaded the
          // whole document. Without JavaScript it still works as a form.
          onSubmit={(event) => {
            event.preventDefault()
            const q = new FormData(event.currentTarget).get("q")
            void navigate({
              search: { q: typeof q === "string" && q ? q : undefined },
            })
          }}
        >
          <Search
            aria-hidden="true"
            className="pointer-events-none absolute top-1/2 left-5 size-5 -translate-y-1/2 text-muted-foreground"
          />
          <input
            ref={field}
            type="search"
            name="q"
            aria-label="Search the help center"
            defaultValue={query}
            placeholder="Search help articles"
            maxLength={100}
            className="h-14 w-full rounded-surface bg-card pr-36 pl-14 text-base text-foreground outline-none placeholder:text-muted-foreground focus-visible:ring-3 focus-visible:ring-ring/50 sm:text-lg [&::-webkit-search-cancel-button]:hidden"
          />
          <div className="absolute top-1/2 right-3 flex -translate-y-1/2 gap-1">
            {query && (
              <Button variant="ghost" render={<Link href="/help" />}>
                Clear
              </Button>
            )}
            <Button type="submit" variant="secondary">
              Search
            </Button>
          </div>
        </form>

        <div className="mt-6">
          {articles === null ? (
            <UnavailableAlert title="Help articles are unavailable right now." />
          ) : articles.length === 0 ? (
            <EmptyState
              icon={<BookOpen />}
              title={
                query ? "No articles match that search" : "No articles yet"
              }
            >
              <div className="flex flex-wrap justify-center gap-2">
                <Button render={<Link href={`${ACCOUNT_URL}/support?new=1`} />}>
                  {contactLabel}
                </Button>
                <Button variant="ghost" render={<Link href="/faq" />}>
                  Read the FAQ
                </Button>
              </div>
            </EmptyState>
          ) : query ? (
            <ArticleGroup name="Results" articles={articles} />
          ) : (
            <div className="grid gap-3 xl:grid-cols-2">
              {groups.map((group) => (
                <ArticleGroup key={group.name} {...group} />
              ))}
            </div>
          )}
        </div>

        {/* The empty state already offers both ways out; repeating them
            right underneath read as a second, identical prompt. */}
        {articles !== null && articles.length > 0 && (
          <StillStuck
            className="mt-3"
            note={
              agentOn &&
              "The assistant answers from these articles and hands anything else to the team."
            }
          >
            <Button render={<Link href={`${ACCOUNT_URL}/support?new=1`} />}>
              {contactLabel}
            </Button>
            <Button variant="secondary" render={<Link href="/faq" />}>
              Read the FAQ
            </Button>
          </StillStuck>
        )}
      </HelpFrame>
    </SiteShell>
  )
}

export const Route = createFileRoute("/help/")({
  validateSearch: (search: Record<string, unknown>): { q?: string } => ({
    q: typeof search.q === "string" ? search.q : undefined,
  }),
  loaderDeps: ({ search }) => ({ q: search.q }),
  // Read per request, as Next rendered this page (it reads the query). The
  // assistant flag comes from Anysola Account, kept 300 s by the service.
  loader: ({ deps }) =>
    getHelp({ data: deps.q?.trim().slice(0, 100) || undefined }),
  head: () =>
    pageHead({
      title: "Help center",
      description:
        "Guides for your Anysola account, billing and support tickets.",
      path: "/help",
      image: "/help/opengraph-image",
    }),
  component: HelpPage,
  pendingComponent: HelpLoading,
})
